import * as React from 'react';
import Primary from './Primary';
import Secondary from './Secondary';
import Tertiary from './Tertiary';
import { ProfileContext } from '../context/ProfileContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import API_URL from '../config';
import Grid from '@mui/material/Grid2';
import { Typography, Paper, Button, Box } from "@mui/material";

export default function QuestionForm(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = React.useContext(ProfileContext);
  const { isCreating, bookName, bookId, editWithNewSelection } = location.state || {};
  const [categoryMap, setCategoryMap] = React.useState({});

  // Track state of primary and secondary category which needs to be passed to each component
  const [primaryCategory, setPrimaryCategory] = React.useState(location.state?.primaryCategory || '');
  const [secondaryCategory, setSecondary] = React.useState([]);

  // Set data for tertiary component
  const [tertiaryCategory, setTertiary] = React.useState([]);

  // descriptions
  const [primaryDescription, setPrimaryDescription] = React.useState("");
  const [secondaryDescription, setSecondaryDescription] = React.useState("");
  const [tertiaryDescription, setTertiaryDescription] = React.useState("");

  // Track if all fields are valid
  const [isValid, setIsValid] = React.useState(false);

  React.useEffect(() => {
    const fetchCategoryMap = async () => {
      try {
        const res = await axiosInstance.get('/categories');
        setCategoryMap(res.data);
      } catch (err) {
        console.log(err);
      }
    };
    fetchCategoryMap();
  }, []);

  React.useEffect(() => {
    if (primaryCategory) {
      setPrimaryCategory(primaryCategory);
    }
  }, [primaryCategory]);

  React.useEffect(() => {
    setIsValid(
      primaryCategory !== '' &&
      secondaryCategory.length > 0 &&
      tertiaryCategory.length > 0
    );
  }, [primaryCategory, secondaryCategory, tertiaryCategory]);  

  React.useEffect(() => {
    if (editWithNewSelection) {
      props.setData(null); // Setze die Daten zurück
    }
  }, [editWithNewSelection]);

  // Store data back in App component
  const handleClick = async () => {
    if (editWithNewSelection) {
      navigate('/pages/ComponentResults', {
        state: {
          bookId,
          bookName,
          primary: primaryCategory,
          secondaries: secondaryCategory,
          tertiaries: tertiaryCategory,
          editWithNewSelection: true,
        },
      });
    } else {
      props.setData({
        primary: primaryCategory,
        secondary: secondaryCategory,
        tertiary: tertiaryCategory,
      });
      navigate('/pages/ComponentResults', { state: { isCreating, bookName } });
    }
  };

  // Define handleChange functions for primary and secondary category
  const handlePrimaryChange = (event) => {
    const newPrimaryCategory = event.target.value;
    setPrimaryCategory(newPrimaryCategory);
    setSecondary([]); // Reset secondary category
    setTertiary([]); // Reset tertiary category data
    setSecondaryContentVisible(false); // Reset visibility of secondary content
    setTertiaryContentVisible(false); // Reset visibility of tertiary content
  };

  const handleSecondaryChange = (event) => {
    const {
      target: { value },
    } = event;
    setSecondary(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
    setTertiary([]); // Reset tertiary category data‚
    setTertiaryContentVisible(false);
  };

  const handleTertiaryChange = (event) => {
    const {
      target: { value },
    } = event;
    setTertiary(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  // define variables and useEffect Hooks for conditionally rendering
  const [secondaryContentVisible, setSecondaryContentVisible] = React.useState(false);
  const [tertiaryContentVisible, setTertiaryContentVisible] = React.useState(false);

  React.useEffect(() => {
    setSecondaryContentVisible(categoryMap?.primaries?.hasOwnProperty(primaryCategory));
    if (primaryCategory && categoryMap?.primaries?.[primaryCategory]) {
      setPrimaryDescription(categoryMap.primaries[primaryCategory].description || "");
    } else {
      setPrimaryDescription("");
    }
  }, [primaryCategory, categoryMap]);  

  React.useEffect(() => {
    setTertiaryContentVisible(secondaryCategory.some(sec => categoryMap?.primaries?.[primaryCategory]?.secondaries?.hasOwnProperty(sec)));
    if (primaryCategory && secondaryCategory.length > 0) {
      const firstSecondary = secondaryCategory[0]; // Falls mehrere, nehme die erste
      if (categoryMap?.primaries?.[primaryCategory]?.secondaries?.[firstSecondary]) {
        setSecondaryDescription(categoryMap.primaries[primaryCategory].secondaries[firstSecondary].description || "");
      } else {
        setSecondaryDescription("");
      }
    } else {
      setSecondaryDescription("");
    }
  }, [secondaryCategory, primaryCategory, categoryMap]);

  React.useEffect(() => {
    if (primaryCategory && secondaryCategory.length > 0 && tertiaryCategory.length > 0) {
      const firstSecondary = secondaryCategory[0]; // Falls mehrere, nehme die erste sekundäre Kategorie
      const firstTertiary = tertiaryCategory[0]; // Falls mehrere, nehme die erste tertiäre Kategorie
      if (categoryMap?.primaries?.[primaryCategory]?.secondaries?.[firstSecondary]?.tertiaries?.[firstTertiary]) {
        setTertiaryDescription(categoryMap.primaries[primaryCategory].secondaries[firstSecondary].tertiaries[firstTertiary].description || "");
      } else {
        setTertiaryDescription("");
      }
    } else {
      setTertiaryDescription("");
    }
  }, [tertiaryCategory, secondaryCategory, primaryCategory, categoryMap]);  

  return (
    <Grid container spacing={4} sx={{ mt: '20vh', maxWidth: '1200px', margin: '10vh auto'}}>
      {/* Erste Zeile mit Primary, Secondary und Tertiary */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ height: '300px', width: '100%', p: 1 }} variant="elevation" elevation={3}>
          <Primary
            primary={primaryCategory}
            handleChange={handlePrimaryChange}
            disabled={editWithNewSelection && userProfile.role !== 'admin'}
            categoryMap={categoryMap}
          />
          {primaryDescription && (
            <Box sx={{ ml: '0.5rem', mr: '2rem', mt: '2rem', p: '1rem', border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '8px', position: 'relative', backgroundColor: '#f9f9f9' }}>
              <Typography variant="caption" sx={{
                position: 'absolute',
                top: '-12px',
                left: '10px',
                px: '5px',
                fontWeight: 'bold',
                color: '#333',
                backgroundImage: 'linear-gradient(to bottom, white 50%, #f9f9f9 50%)',
                backgroundSize: '100% 100%',
                backgroundClip: 'padding-box',
                padding: '2px 5px',
              }}>
                Beschreibung
              </Typography>

              <Typography variant="body2" sx={{ color: '#555' }}>
                {primaryDescription}
              </Typography>
            </Box>
          )}

        </Paper>
      </Grid>
  
      {secondaryContentVisible && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ height: '300px', width: '100%', p: 1 }} variant="elevation" elevation={3}>
            <Secondary primary={primaryCategory} secondary={secondaryCategory} handleChange={handleSecondaryChange} categoryMap={categoryMap}/>
            {secondaryDescription && (
            <Box sx={{ ml: '0.5rem', mr: '2rem', mt: '2rem', p: '1rem', border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '8px', position: 'relative', backgroundColor: '#f9f9f9' }}>
              <Typography variant="caption" sx={{
                position: 'absolute',
                top: '-12px',
                left: '10px',
                px: '5px',
                fontWeight: 'bold',
                color: '#333',
                backgroundImage: 'linear-gradient(to bottom, white 50%, #f9f9f9 50%)',
                backgroundSize: '100% 100%',
                backgroundClip: 'padding-box',
                padding: '2px 5px',
              }}>
                Beschreibung
              </Typography>
              <Typography variant="body2" sx={{ color: '#555' }}>
                {secondaryDescription}
              </Typography>
            </Box>
          )}
          </Paper>
        </Grid>
      )}
  
      {tertiaryContentVisible && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ height: '300px', width: '100%', p: 1 }} variant="elevation" elevation={3}>
          <Typography variant="subtitle1" sx={{ mt: '1rem', ml: '5px', mb: '1rem' }}>Wählen Sie nun aus folgenden Unterkategorien</Typography>
            {secondaryCategory.map((secondaryCat, index) => (
              <Tertiary key={index} primary={primaryCategory} secondary={secondaryCat} tertiary={tertiaryCategory} handleChange={handleTertiaryChange} categoryMap={categoryMap}/>
            ))}
            {tertiaryDescription && (
            <Box sx={{ ml: '0.5rem', mr: '2rem', mt: '2rem', p: '1rem', border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '8px', position: 'relative', backgroundColor: '#f9f9f9' }}>
              <Typography variant="caption" sx={{
                position: 'absolute',
                top: '-12px',
                left: '10px',
                px: '5px',
                fontWeight: 'bold',
                color: '#333',
                backgroundImage: 'linear-gradient(to bottom, white 50%, #f9f9f9 50%)',
                backgroundSize: '100% 100%',
                backgroundClip: 'padding-box',
                padding: '2px 5px',
              }}>
                Beschreibung
              </Typography>
              <Typography variant="body2" sx={{ color: '#555' }}>
                {tertiaryDescription}
              </Typography>
            </Box>
          )}
          </Paper>
        </Grid>
      )}

      {tertiaryContentVisible && (
        <Grid size={{ xs: 12, md: 12 }} sx={{ textAlign: 'center'}}>
          <Button 
            variant="contained" 
            data-testid="submit-button" 
            disabled={!isValid} 
            onClick={handleClick}
            sx={{ maxWidth: '200px', width: '100%' }}
          >
            Fertig
          </Button>
        </Grid>
      )}
    </Grid>
  );
}